import { Icon, spacing, Text } from '@scality/core-ui';
// core-ui's own button parts: `Button` forwards no ref, and the menu must anchor to the element.
import {
  ButtonIcon,
  ButtonLabel,
  ButtonLoader,
  ButtonStyled,
} from '@scality/core-ui/dist/components/buttonv2/Buttonv2.component';
import { useSelect } from 'downshift';
import type React from 'react';
import styled from 'styled-components';
import {
  mergeRefs,
  SelectMenuPanel,
  SelectMenuPlainList,
  SelectMenuRow,
  SelectMenuScroll,
  SelectWrapper,
  useFloatingSelectMenu,
} from '../../components/select';
import type { ExportFormat } from './renderToFile';

const TRIGGER_LABEL = 'Export';

const Trigger = styled(ButtonStyled)`
  /* Fixed: the icon swaps to a narrower loader, and the button would resize under the pointer. */
  min-width: 7rem;
`;

const Caret = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: ${spacing.r8};
`;

/** Explicit width, never a percentage: the panel is `fixed`, so `100%` is the whole viewport. */
const Menu = styled(SelectMenuPanel)`
  min-width: 11rem;
  width: max-content;
`;

const FORMATS: { format: ExportFormat; label: string }[] = [
  { format: 'png', label: 'PNG image' },
  { format: 'pdf', label: 'PDF document' },
];

type DashboardExportMenuProps = {
  /** The format being written, or `null` when idle. */
  exportingFormat: ExportFormat | null;
  /** True while the dashboard has nothing rendered to capture. */
  disabled: boolean;
  onExport: (format: ExportFormat) => void;
};

/**
 * Built on the same downshift + floating-panel pieces as the criteria controls beside it; only the
 * trigger differs, an action belonging on a button rather than behind a select's field.
 */
export const DashboardExportMenu: React.FC<DashboardExportMenuProps> = ({ exportingFormat, disabled, onExport }) => {
  const isExporting = exportingFormat !== null;
  // Stays in the header with nothing to export, so the action's place doesn't move under the eye.
  const isDisabled = disabled || isExporting;

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect({
    items: FORMATS,
    itemToString: (item) => item?.label ?? '',
    // Held at null so the same format can be picked twice: exporting is an action, not a choice.
    selectedItem: null,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onExport(selectedItem.format);
    },
  });

  const { refs: floatingRefs, floatingStyles } = useFloatingSelectMenu({
    isOpen,
    preferredBelow: true,
    mainAxisAbovePx: 8,
    mainAxisBelowPx: 8,
  });

  const { ref: menuRef, style: menuStyle, ...menuRest } = getMenuProps();
  // `role="combobox"` takes no name from its contents, and downshift points it at a label element
  // the criteria controls have but this one does not — without both lines it has no name at all.
  const { ref: toggleRef, ...toggleRest } = getToggleButtonProps({
    disabled: isDisabled,
    'aria-label': TRIGGER_LABEL,
    'aria-labelledby': undefined,
  });

  return (
    <SelectWrapper>
      <Trigger
        type="button"
        ref={mergeRefs(floatingRefs.setReference, toggleRef)}
        {...toggleRest}
        $variant="secondary"
        $label={TRIGGER_LABEL}
        disabled={isDisabled}
      >
        {isExporting ? (
          <ButtonLoader size="small" $variant="secondary" $label={TRIGGER_LABEL} />
        ) : (
          <ButtonIcon $label={TRIGGER_LABEL}>
            <Icon name="Export" />
          </ButtonIcon>
        )}
        <ButtonLabel>{TRIGGER_LABEL}</ButtonLabel>
        <Caret>
          <Icon name="Dropdown-down" size="xs" />
        </Caret>
      </Trigger>
      <Menu
        ref={mergeRefs(floatingRefs.setFloating, menuRef)}
        {...menuRest}
        style={{ ...menuStyle, ...floatingStyles }}
        aria-hidden={!isOpen}
        $isOpen={isOpen}
      >
        <SelectMenuScroll>
          <SelectMenuPlainList>
            {isOpen &&
              FORMATS.map((item, index) => (
                <SelectMenuRow
                  key={item.format}
                  $highlighted={highlightedIndex === index}
                  {...getItemProps({ item, index })}
                >
                  <Text>{item.label}</Text>
                </SelectMenuRow>
              ))}
          </SelectMenuPlainList>
        </SelectMenuScroll>
      </Menu>
    </SelectWrapper>
  );
};
